import { h, ref, nextTick } from 'vue'
import type { Component } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import http from "@/api/request"
import formCreate, { type Api, type Rule, type Options } from '@form-create/element-ui'
import type { AxiosResponse } from 'axios'

// 更精确的类型定义
interface UploadResponse {
    status: number
    data: {
        src: string
    }
}

interface ApiResponse {
    status: number
    message: string
    data: {
        rule: Rule[]
        action: string
        method: string
        title: string
        config: Options
        api?: string
        formData?: Record<string, any>
    }
}

interface FormSubmitResponse {
    status: number
    msg: string
    data: any
}

interface ModalFormConfig {
    title?: string
    width?: string
    height?: string
    onSuccess?: (response: FormSubmitResponse) => void
    onError?: (error: any) => void
}

const cloneJson = <T,>(val: T): T => {
    try {
        // structuredClone 对普通 JSON 规则足够；失败则回退 JSON
        return typeof structuredClone === 'function' ? structuredClone(val) : JSON.parse(JSON.stringify(val))
    } catch {
        return JSON.parse(JSON.stringify(val))
    }
}

/**
 * EB 后端会返回 type=img + attrs.src 的“原生图片”规则。
 * Vue3 + @form-create/element-ui 下经常无法稳定渲染该类型，这里统一转为 el-image。
 */
const normalizeImgRule = (rule: any) => {
    if (!rule || typeof rule !== 'object') return
    if (rule.type !== 'img') return

    const src = String(rule?.attrs?.src || rule?.props?.src || '')
    const styleObj = { ...(rule.style || {}), ...(rule.props?.style || {}) }
    const styleStr = Object.entries(styleObj)
        .map(([k, v]) => `${k}:${String(v)}`)
        .join(';')

    // form-create/core 的 html 渲染器不会读 props.innerHTML，只会把 children 里的字符串拼成 innerHTML
    rule.type = 'html'
    rule.props = { tag: 'div' }
    rule.children = src
        ? [`<img src="${src.replace(/"/g, '&quot;')}" style="${styleStr.replace(/"/g, '&quot;')}" />`]
        : []
    delete rule.attrs
    delete rule.style
}

/** 收集带 value 的扁平 options（兼容分组 { label, options: [...] }） */
const collectFlatSelectOptions = (options: any): any[] => {
    if (!Array.isArray(options) || !options.length) return []
    const out: any[] = []
    for (const o of options) {
        if (!o || typeof o !== 'object') continue
        if (Array.isArray(o.options) && o.options.length) {
            out.push(...collectFlatSelectOptions(o.options))
        } else if ('value' in o) {
            out.push(o)
        }
    }
    return out
}

const SELECT_LIKE_TYPES = new Set(['radio', 'select', 'checkbox'])

/**
 * 后端/库存里常见 number，options 里却是 string（或反之），Element Plus 用 === 绑定会选不中。
 * 在 options 中找「字符串相等」的项，把 rule.value / defaultValue / control[].value 统一成该项的原始类型。
 */
const normalizeSelectLikeRuleValues = (rule: any) => {
    if (!rule || typeof rule !== 'object') return
    if (!SELECT_LIKE_TYPES.has(String(rule.type))) return
    const flat = collectFlatSelectOptions(rule.options)
    if (!flat.length) return

    const alignScalar = (v: any): any => {
        if (v === undefined || v === null || v === '') return v
        for (const opt of flat) {
            const ov = opt.value
            if (ov === v) return v
            if (String(ov) === String(v)) return ov
        }
        return v
    }

    const t = String(rule.type)
    if (t === 'checkbox' && Array.isArray(rule.value)) {
        rule.value = rule.value.map((x: any) => alignScalar(x))
    } else {
        const nv = alignScalar(rule.value)
        if (nv !== rule.value) rule.value = nv
    }

    if (rule.props) {
        const dv = rule.props.defaultValue
        if (t === 'checkbox' && Array.isArray(dv)) {
            rule.props.defaultValue = dv.map((x: any) => alignScalar(x))
        } else if (dv !== undefined && dv !== null && dv !== '') {
            const nd = alignScalar(dv)
            if (nd !== dv) rule.props.defaultValue = nd
        }
    }

    if (Array.isArray(rule.control)) {
        for (const c of rule.control) {
            if (c && c.value !== undefined && c.value !== null && c.value !== '') {
                const cv = alignScalar(c.value)
                if (cv !== c.value) c.value = cv
            }
        }
    }
}

const walkRules = (rules: any[]) => {
    if (!Array.isArray(rules)) return
    for (const rule of rules) {
        normalizeSelectLikeRuleValues(rule)
        normalizeImgRule(rule)

        // control 联动子规则
        if (Array.isArray(rule.control)) {
            for (const c of rule.control) {
                if (c && Array.isArray(c.rule)) walkRules(c.rule)
            }
        }

        // 一些结构会用 children 嵌套子规则
        if (Array.isArray(rule.children)) {
            const nested = rule.children.filter((x: any) => x && typeof x === 'object')
            if (nested.length) walkRules(nested)
        }
    }
}

/** 对后端下发的 form-create 规则做统一修补（img 渲染、radio/select 值类型对齐等），页面拉取 rule 后也可调用 */
export function applyFormCreateRuleFixes(rules: any[]) {
    walkRules(rules)
}

/**
 * 创建模态表单
 * @param api 表单API函数
 * @param config 配置选项
 * @returns Promise
 */
export default function modalForm(
    api: () => Promise<AxiosResponse<ApiResponse>>,
    config: ModalFormConfig = {}
): Promise<FormSubmitResponse> {
    return new Promise((resolve, reject) => {
        const { title = '表单', onSuccess, onError } = config
        const formApiRef = ref<Api | null>(null)

        // 获取表单数据
        api().then((response) => {
            const responseData = response.data.data || response.data
            const data = responseData

            if (!data.config) data.config = {}

            // 处理表单规则
            const rules = data.rule || []
            const processedRules = rules.map((rule: any) => {
                const r = cloneJson(rule)

                // modalForm 默认：每个表单项占满一行（避免 messageBox 内控件过窄）
                // 后端若已显式传 col/span，则尊重后端配置
                if (!r.col) {
                    r.col = { span: 24 }
                } else if (typeof r.col === 'number') {
                    r.col = { span: r.col }
                } else if (r.col && typeof r.col === 'object' && r.col.span == null) {
                    r.col = { ...r.col, span: 24 }
                }

                // 尽量保证输入类控件宽度充满当前列
                // 注意：img/elImage 这类展示组件不要强行 width:100%，否则容易把预览/布局搞乱
                const skipFullWidthTypes = new Set(['img', 'html', 'upload', 'frame'])
                if (!skipFullWidthTypes.has(String(r.type))) {
                    if (!r.props) r.props = {}
                    if (!r.props.style) r.props.style = {}
                    r.props.style = { width: '100%', ...(r.props.style || {}) }
                }

                // 处理上传组件的回调
                if (r.type === 'frame' && r.props && r.props.type === 'image') {
                    r.props.onSuccess = (response: UploadResponse) => {
                        if (response.status === 200) {
                            return response.data.src
                        }
                        ElMessage.error('上传失败')
                        return ''
                    }
                    r.props.onLoad = (e: any) => {
                        if (e.target) {
                            e.target.style.width = '100%'
                            e.target.style.height = '100%'
                        }
                    }
                }
                
                // 确保suffix正确显示
                if (r.suffix) {
                    // 保持suffix的原始结构
                    r.suffix = {
                        ...r.suffix,
                        style: {
                            ...r.suffix.style,
                            display: 'block'
                        }
                    }
                }
                
                // 确保inputNumber的controls正确显示
                if (r.type === 'inputNumber' && r.props) {
                    r.props = {
                        ...r.props,
                        controls: true,
                        controlsPosition: 'right'
                    }
                }
                
                return r
            })

            // 将 EB 的 img 规则递归转换为 form-create 的 html 渲染（含 control 内嵌套）
            walkRules(processedRules)

            // 创建表单配置
            const formConfig = {
                rule: processedRules,
                option: {
                    submitBtn: false,
                    resetBtn: false,
                    form: {
                        labelWidth: '110px',
                        size: 'small',
                    },
                    formData: {},
                    global: {
                        frame: {
                            props: {
                                closeBtn: false,
                                maskClosable: false
                            }
                        },
                        inputNumber: {
                            props: {
                                min: 0,
                                controls: true,
                                controlsPosition: 'right'
                            }
                        },
                        upload: {
                            props: {
                                accept: 'image/*',
                                maxSize: 2 * 1024 * 1024,
                                onError: (err: any) => {
                                    ElMessage.error(err.message || '上传失败')
                                }
                            }
                        }
                    }
                }
            }

            // 创建模态框
            ElMessageBox({
                title,
                // 与 EB 结构保持一致：内容容器使用 common-form-create
                message: h('div', { class: 'common-form-create form-container' }, [
                    h(formCreate, {
                        ...formConfig,
                        onMounted: (fApi: Api) => {
                            // console.log('Form mounted, fApi:', fApi)
                            formApiRef.value = fApi
                        }
                    })
                ]),
                showCancelButton: true,
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                // 兼容：Element Plus MessageBox 的 class 落点可能不同，统一追加 modal-form + modal-form-dialog
                // - modal-form：对齐 EB
                // - modal-form-dialog：沿用本项目之前的选择器
                customClass: `${(config as any).class || ''} modal-form modal-form-dialog`.trim(),
                beforeClose: async (action, instance, done) => {
                    if (action === 'confirm') {
                        // console.log('Confirm clicked, formApiRef.value:', formApiRef.value)
                        
                        // 等待下一个tick，确保表单完全加载
                        await nextTick()
                        
                        // 尝试从DOM中获取表单API
                        if (!formApiRef.value) {
                            const formElement = document.querySelector('.form-container')
                            if (formElement) {
                                const formInstance = (formElement as any).__vueParentComponent?.ctx
                                if (formInstance && formInstance.$f) {
                                    formApiRef.value = formInstance.$f
                                    // console.log('Found form API from DOM:', formApiRef.value)
                                }
                            }
                        }
                        
                        if (formApiRef.value) {
                            formApiRef.value.submit((formData: any) => {
                                // 对齐 EB / admin modal-form：只提交用户填写值，勿用后端回填 formData 覆盖
                                const submitData = { ...formData }

                                // 确保pid字段是整数类型
                                if (submitData.pid !== undefined && submitData.pid !== null && submitData.pid !== '') {
                                    submitData.pid = Number(submitData.pid);
                                }

                                // console.log('Submitting data:', submitData);

                                // 优先用 processApiPath 后的 api（无端前缀）；去掉首 /，避免 axios 拼到站点根路径丢掉 /mer
                                const rawUrl = String(data.api || data.action || '').replace(/^\//, '')
                                http({
                                    url: rawUrl,
                                    method: data.method.toLowerCase(),
                                    data: submitData
                                }).then((submitResponse: any) => {
                                    // console.log('Submit response:', submitResponse)
                                    
                                    // axios 响应拦截器返回整包 { code, message, data }，与 EB 一致
                                    const result = submitResponse
                                    
                                    // 适配不同的响应格式
                                    let status = result.status || result.code || 200  // 如果没有status/code，默认为200
                                    let message = result.message || result.msg || '操作成功'
                                    let responseData = result.data
                                    
                                    // console.log('Processed result:', { status, message, data: responseData })
                                    
                                    if (status === 200) {
                                        ElMessage.success(message || '操作成功')
                                        onSuccess?.({ status, msg: message, data: responseData })
                                        resolve({ status, msg: message, data: responseData })
                                        done()
                                    } else {
                                        ElMessage.error(message || '操作失败')
                                        onError?.({ status, msg: message, data: responseData })
                                        reject({ status, msg: message, data: responseData })
                                    }
                                }).catch((err: any) => {
                                    console.error('Submit error:', err)
                                    ElMessage.error(err.message || '操作失败')
                                    onError?.(err)
                                    reject(err)
                                })
                            })
                        } else {
                            console.error('No form API available')
                            ElMessage.error('表单未初始化')
                            reject(new Error('表单未初始化'))
                        }
                    } else {
                        done()
                    }
                }
            }).catch((err: any) => {
                if (err !== 'cancel') {
                    ElMessage.error(err.message || '操作失败')
                    onError?.(err)
                    reject(err)
                }
            })
        }).catch((err: any) => {
            ElMessage.error(err.message || '加载表单失败')
            onError?.(err)
            reject(err)
        })
    })
}

export type { Api, Options, ApiResponse, ModalFormConfig, Rule }